import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  HiOutlineDocumentText,
  HiOutlineArrowDownTray,
  HiOutlineArrowPath,
  HiOutlineExclamationTriangle,
  HiOutlineArrowTrendingUp,
  HiOutlineCurrencyDollar,
  HiOutlineArrowsRightLeft,
  HiOutlineRectangleStack,
  HiOutlineArrowLeft,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineCalendar,
} from 'react-icons/hi2';
import sparePartsService from '../services/sparePartsService';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
import PageLoading from '../components/common/PageLoading';
import './Reports.css';

// ─── Report Definitions ───────────────────────────────────────────
const REPORT_TYPES = [
  {
    id: 'low-stock',
    label: 'Low Stock Report',
    icon: HiOutlineExclamationTriangle,
    color: '#ef4444',
    desc: 'Parts where current stock ≤ minimum stock',
  },
  {
    id: 'overstock',
    label: 'Overstock Report',
    icon: HiOutlineArrowTrendingUp,
    color: '#f59e0b',
    desc: 'Parts where current stock > 3× minimum stock',
  },
  {
    id: 'valuation',
    label: 'Inventory Valuation',
    icon: HiOutlineCurrencyDollar,
    color: SEMANTIC_SUCCESS_MID,
    desc: 'Stock quantity × last purchase unit cost per part',
  },
  {
    id: 'movement',
    label: 'Stock Movement Report',
    icon: HiOutlineArrowsRightLeft,
    color: '#6366f1',
    desc: 'All transactions with part, type and cost details',
  },
];

// ─── Column definitions per report type ──────────────────────────
const COLUMNS = {
  'low-stock': [
    { key: 'partNumber', label: 'Part Number' },
    { key: 'partName', label: 'Part Name' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'uom', label: 'UOM' },
    { key: 'currentStock', label: 'Current Stock', num: true },
    { key: 'minimumStock', label: 'Min Stock', num: true },
    { key: 'shortage', label: 'Shortage', num: true, highlight: '#ef4444' },
    { key: 'status', label: 'Status' },
  ],
  overstock: [
    { key: 'partNumber', label: 'Part Number' },
    { key: 'partName', label: 'Part Name' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'uom', label: 'UOM' },
    { key: 'currentStock', label: 'Current Stock', num: true },
    { key: 'minimumStock', label: 'Min Stock', num: true },
    { key: 'maxStock', label: 'Max Stock (3×)', num: true },
    { key: 'excessStock', label: 'Excess Stock', num: true, highlight: '#f59e0b' },
    { key: 'unitCost', label: 'Unit Cost', num: true, prefix: '₹' },
    { key: 'excessValue', label: 'Excess Value', num: true, prefix: '₹', highlight: '#f59e0b' },
  ],
  valuation: [
    { key: 'partNumber', label: 'Part Number' },
    { key: 'partName', label: 'Part Name' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'uom', label: 'UOM' },
    { key: 'status', label: 'Status' },
    { key: 'currentStock', label: 'Stock', num: true },
    { key: 'unitCost', label: 'Unit Cost', num: true, prefix: '₹' },
    { key: 'inventoryValue', label: 'Inventory Value', num: true, prefix: '₹', highlight: SEMANTIC_SUCCESS_MID },
  ],
  movement: [
    { key: 'transactionDate', label: 'Date', date: true },
    { key: 'partNumber', label: 'Part Number' },
    { key: 'partName', label: 'Part Name' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'transactionType', label: 'Type', badge: true },
    { key: 'quantity', label: 'Quantity', num: true },
    { key: 'unitCost', label: 'Unit Cost', num: true, prefix: '₹' },
    { key: 'totalCost', label: 'Total Cost', num: true, prefix: '₹' },
    { key: 'remarks', label: 'Remarks' },
  ],
};

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }));

const CellValue = ({ col, value }) => {
  if (value == null || value === '') return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  if (col.date) {
    return <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
      {new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
    </span>;
  }
  if (col.badge) {
    const map = { Purchase: SEMANTIC_SUCCESS_MID, Issue: '#ef4444', Adjustment: '#f59e0b' };
    const c = map[value] || '#6366f1';
    return <span style={{ padding: '2px 10px', borderRadius: 999, background: c + '20', color: c, fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</span>;
  }
  if (col.num) {
    const n = Number(value);
    return <span style={{ color: col.highlight || 'var(--text-primary)', fontWeight: 600 }}>
      {col.prefix}{fmt(n)}
    </span>;
  }
  return <span>{value}</span>;
};

const SparePartsReports = () => {
  const navigate = useNavigate();
  const { reportType } = useParams();
  const selectedReport = reportType || 'low-stock';
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [parts, setParts] = useState([]);
  const [showFilters, setShowFilters] = useState(true);

  // Movement report filters
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [selectedPart, setSelectedPart] = useState('');
  const [movementType, setMovementType] = useState('All');

  useEffect(() => {
    sparePartsService.getParts().then(setParts).catch(() => { });
  }, []);

  const loadReport = useCallback(async (opts = {}) => {
    const notify = opts.notify === true;
    if (notify) showReportGeneratingToast();
    try {
      setLoading(true);
      setError('');
      setData([]);
      let res;
      if (selectedReport === 'low-stock') res = await sparePartsService.getLowStockReport();
      else if (selectedReport === 'overstock') res = await sparePartsService.getOverstockReport();
      else if (selectedReport === 'valuation') res = await sparePartsService.getValuationReport();
      else if (selectedReport === 'movement') {
        res = await sparePartsService.getMovementReport({
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          partId: selectedPart || undefined,
          movementType: movementType !== 'All' ? movementType : undefined,
        });
      }
      setData(res?.data || []);
      if (notify) reportGenerationDone();
    } catch (err) {
      console.error(err);
      const msg = 'Failed to generate report. Please try again.';
      setError(msg);
      if (notify) reportGenerationFailed(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedReport, startDate, endDate, selectedPart, movementType]);

  useEffect(() => {
    // Only auto-load for reports that don't need complex date filters on initial mount/switch
    if (selectedReport !== 'movement') {
      loadReport({ notify: false });
    }
  }, [selectedReport, loadReport]);

  const exportCSV = () => {
    if (data.length === 0) return;
    const cols = COLUMNS[selectedReport];
    const headers = cols.map(c => c.label).join(',');
    const rows = data.map(row =>
      cols.map(c => {
        const v = row[c.key];
        if (v == null) return '';
        if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
        return v;
      }).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    window.print();
  };

  const activeReport = REPORT_TYPES.find(r => r.id === selectedReport);
  const cols = COLUMNS[selectedReport] || [];
  return (
    <div className="pagecontainer report-page">
      {/* Header */}
      <div className="page-header page-header--report-tight">
        <div className="oee-report-header-left">
          <button className="oee-report-back-btn mr-2" onClick={() => navigate('/home/reports')}>
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              {activeReport && React.createElement(activeReport.icon)} {activeReport?.label || 'Inventory Report'}
            </h1>
            <p className="oee-report-subtitle">{activeReport?.desc}</p>
          </div>
        </div>

        <div className="header-actions">
           {selectedReport === 'movement' && (
              <button className="oee-filter-toggle-btn" onClick={() => setShowFilters(p => !p)}>
                <HiOutlineDocumentText size={16} /> Filters {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
              </button>
           )}
          <button type="button" className="btn-secondary btn" onClick={() => loadReport({ notify: true })} disabled={loading}>
            <HiOutlineArrowPath /> Refresh
          </button>
          <button type="button" className="btn-primary" onClick={exportCSV} disabled={data.length === 0}>
            <HiOutlineArrowDownTray /> Export
          </button>
        </div>
      </div>

      {/* Filters for movement report */}
      {selectedReport === 'movement' && showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>From Date</label>
              <input type="date" value={startDate} className="filter-input" onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input type="date" value={endDate} className="filter-input" onChange={e => setEndDate(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>Spare Part</label>
              <select value={selectedPart} className="filter-input" style={{ appearance: 'auto' }} onChange={e => setSelectedPart(e.target.value)}>
                <option value="">All Parts</option>
                {parts.map(p => (
                  <option key={p.sparePartId} value={p.sparePartId}>
                    {p.partNumber} – {p.partName}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Movement Type</label>
              <select value={movementType} className="filter-input" style={{ appearance: 'auto' }} onChange={e => setMovementType(e.target.value)}>
                {['All', 'Purchase', 'Issue', 'Adjustment'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="filter-group report-filter-group--actions">
              <label>&nbsp;</label>
              <button type="button" className="btn-primary report-generate-btn" onClick={() => loadReport({ notify: true })} disabled={loading}>
                <HiOutlineCalendar /> Generate Report
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="alert alert-error mb-4">{error}</div>}

      {/* Report Table */}
      {loading ? (
        <PageLoading message={`Loading ${activeReport?.label || 'Inventory Report'} data…`} />
      ) : data.length === 0 ? (
        <div className="report-card report-state-block">
          <HiOutlineRectangleStack size={48} className="report-state-block__icon" aria-hidden />
          <p className="report-state-block__muted">No records found for current criteria.</p>
        </div>
      ) : (
        <div className="report-table-shell">
          <div className="report-table-shell__head report-table-shell__head--split">
            <h3>{activeReport?.label} Data</h3>
            <span className="report-table-shell__head-meta">
              {data.length.toLocaleString()} record(s) found
            </span>
          </div>
          <div className="report-table-scroll">
            <table className="report-table">
              <thead>
                <tr>
                  <th>#</th>
                  {cols.map(c => (
                    <th key={c.key} style={c.num ? { textAlign: 'right' } : {}}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    {cols.map(c => (
                      <td key={c.key} style={c.num ? { textAlign: 'right' } : {}}>
                        <CellValue col={c} value={row[c.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>

  );
};

export default SparePartsReports;
